import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    // Reuse the provided id, fall back to the name attribute, or autogenerate.
    const inputId = id ?? props.name ?? `input-${Math.random().toString(36).slice(2, 9)}`;
    return (
      <div className="space-y-1.5">
        {label ? (
          <label htmlFor={inputId} className="block text-[13px] font-medium text-kw-label">
            {label}
          </label>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'block w-full rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark',
            'placeholder:text-kw-muted',
            'focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20',
            'disabled:bg-kw-bg disabled:text-kw-muted',
            error && 'border-kw-danger-text focus:border-kw-danger-text focus:ring-kw-danger-text/20',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />
        {error ? (
          <p id={`${inputId}-error`} className="text-[12px] text-kw-danger-text">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-[12px] text-kw-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
